import React, { useContext, useCallback, useState } from "react";
import ContextMenuInteraction from "./MapContextMenu";
import { Context as InteractionContext } from "../../InteractionContext";
import SelectFeatureOnHover from "../../Select/SelectFeatureOnHover";
import { Context as SelectionContext } from "../../Select/SelectFeature";
import { Context as DataSourceContext } from "../../../DataSourceContext";
import { useMediaQuery } from 'react-responsive';
import TenantFeatureContext from "../../../../../contexts/TenantContext";
import { MapEditorRouteContext } from "../../../../CustomRoutes/MapEditor";
import { isInspectionSigned } from "../../../../../contexts/InspectionContext";
import {
  isLegendItemType,
  LEGEND_ITEM_TYPES,
} from "../../../Positionable/legendItemType";
/**
 * Switches interaction mode
 */
function FeatureMenuUI({ ...props }) {
  const dataSourceContext = useContext(DataSourceContext);
  const isTabletOrLess = useMediaQuery({ maxWidth: 992 });
  const setDimension = () => ({ width: window.innerWidth });
  const [isWidth, setIsWidth] = useState(isTabletOrLess ? true : false);
  const { getDataTypeLabel } = dataSourceContext;
  const {
    setMode,
    setPositionableType,
    setPositionableSourceModel,
    positionableSourceModel
} = useContext(InteractionContext);

  const selectionContext = useContext(SelectionContext);
  const featureContext = useContext(TenantFeatureContext);
  const mapEditorRouteContext = useContext(MapEditorRouteContext);

  /**
   * Once a compliance certification is signed the inspection is closed to new findings — the
   * Findings tab drops its "Add Finding" button and the map editor's findings panel drops its "+".
   * The same rule has to reach the map context menus, or the menu becomes a way around the gate.
   *
   * Reuses `isInspectionSigned` (certification_date && compliance_date), the same predicate behind
   * `inspectionContext.readOnlyInspection` and the `readOnly` flag MapEditor already puts on the
   * finding source-model form.
   */
  const isSignedInspection =
    !!mapEditorRouteContext.selectedInspection &&
    isInspectionSigned(mapEditorRouteContext.selectedInspection);

  /**
   * SW2-2346 — edits are allowed on the newest map version only.
   *
   * The inspection-based test used below fails open on a project with no inspections:
   * selectedInspectionId is undefined, so allowEditDueToSpecialInspection is true and
   * nothing is ever disabled. Version is the right axis and it covers both project
   * shapes. Undefined means no version is chosen yet, which must not block anything.
   *
   * Read here rather than inside each callback so the callbacks can depend on a plain
   * boolean. Depending on `mapEditorRouteContext` itself would rebuild them on every
   * provider render, and MapContextMenu re-binds its map listeners whenever these
   * identities change; depending on nothing at all is how the menu came to keep the
   * previous version's answer after the picker moved.
   */
  const onOlderMapVersion = mapEditorRouteContext.isLatestMapVersion === false;

  const newDimension = () => {
    if (window.innerWidth < 992) {
      setIsWidth(true);
      setDimension({ width: window.innerWidth });
    } else {
      setIsWidth(false);
    }
  };

  window.addEventListener('resize', newDimension);
  const isFeatureReadOnly = useCallback(
    selectedFeature => {
      const type = selectedFeature.get("positionable_type");
      const isArchivedAsset =
        type === "asset_type" &&
        !!dataSourceContext.getById(selectedFeature.get("positionable_source_id"), type)?.archived_at;

      return dataSourceContext.isReadOnlyDataType(type) || isArchivedAsset;
    },
    [dataSourceContext],
  );

  /**
   * SW2-2230 — "Add Finding" against a *specific* placed legend item.
   *
   * Returns a single-element array (or an empty one) so callers can spread it into a menu without
   * a conditional. Offered for legend item features only — control measures and pollutants — since
   * those are the things a finding can be filed against.
   *
   * The clicked feature knows the legend item (`positionable_source_id`) and the live positionables
   * row (`positionables_id`). Both are handed to the finding form; it swaps the live row for that
   * placement's edit-chain head once the placement list loads, because the two differ for anything
   * that has been edited.
   */
  const makeAddFindingOptions = useCallback(
    contextmenuFeature => {
      if (!contextmenuFeature) {
        return [];
      }

      // A map feature carries `positionable_type` straight from its positionables row, so a legend
      // item reads "legend_items" — the subtype ("control_measure" / "pollutant") lives on
      // `_context`. Accept every spelling: getProjectUploadOptions below tests only the subtypes,
      // which is why its menu never matches a legend item. See legendItemType.js.
      const positionableType =
        contextmenuFeature.values_?.positionable_type ||
        contextmenuFeature.get?.("positionable_type");

      // Guard against a legend item that has not been saved yet — it has no placement to file
      // against, and prefilling a temp id would dangle.
      if (
        !isLegendItemType(positionableType) ||
        !contextmenuFeature.get("positionables_id")
      ) {
        return [];
      }

      // Read-only is checked *here*, on the feature this menu entry is actually about, rather than
      // relying on the callers' guards. Two reasons those did not cover it:
      //
      //  - they test `selectedFeature`, which comes from hover selection and is null on the
      //    right-click/touch path this file documents elsewhere — so the guard simply did not run;
      //  - `isReadOnlyDataType` resolves against the names data sources are *registered* under,
      //    which are the subtypes (MapEditor.js registers "control_measure" / "pollutant"). A
      //    feature whose `positionable_type` reads "legend_items" matched nothing and came back
      //    "not read only", so a read-only user was offered Add Finding.
      //
      // Hence testing every legend spelling rather than trying to pick the right one. Which one a
      // given feature carries depends on whether it came through PositionableDataSource (which
      // rewrites the field to the subtype) or was built by the map layer from its row — and
      // `_context`, which would disambiguate, is only ever set on save payloads, never on a
      // feature. Read-only is a per-project permission applied uniformly to both legend data
      // sources, so if any of them is read-only, legend items are read-only for this user.
      const isReadOnly = LEGEND_ITEM_TYPES.some((type) =>
        dataSourceContext.isReadOnlyDataType(type),
      );

      if (isReadOnly) {
        return [];
      }

      // Only Full Projects have an in-app inspection for a finding to attach to.
      const projectConfig = mapEditorRouteContext.project?.project_config || "full";

      if (projectConfig !== "full" || isSignedInspection) {
        return [];
      }

      const hasSelectedInspection = !!mapEditorRouteContext.selectedInspection;

      return [
        {
          action: "addFinding",
          positionableType: "findings",
          label: "Add Finding",
          onClick: clickEvent => {
            const feature =
              contextmenuFeature ||
              selectionContext.getSelectedFeature() ||
              clickEvent?.feature;

            if (!feature) {
              return;
            }

            setPositionableType("findings");
            setMode("createSourceModel", {
              nextMode: "create",
              mapEvent: clickEvent,
              legendPrefill: {
                legend_item_id: feature.get("positionable_source_id"),
                live_positionable_id: feature.get("positionables_id"),
              },
            });
          },
          isDisabled: !hasSelectedInspection,
          disabledMessage: !hasSelectedInspection
            ? "Please select an inspection before adding a finding."
            : null,
        },
      ];
    },
    [
      dataSourceContext,
      mapEditorRouteContext,
      selectionContext,
      setMode,
      setPositionableType,
      isSignedInspection,
    ],
  );

  /**
   * @todo lets get this method refactor
   */

  const getOptions = useCallback(
    (event, contextmenuFeature) => {
      const geometryType =
        contextmenuFeature && contextmenuFeature.get("geometry").getType();
      const selectedFeature = selectionContext.getSelectedFeature();
      const isRecentInsp =
        mapEditorRouteContext.inspections?.length > 0 &&
        mapEditorRouteContext.inspections[0].id === mapEditorRouteContext.selectedInspection?.id;
      const selectedInspectionId = mapEditorRouteContext.selectedInspection?.id;
      const isInitialInspection = mapEditorRouteContext.inspections?.length === 1;
      const allowEditDueToSpecialInspection = !(selectedInspectionId) || isInitialInspection;
      const hasSelectedInspection = !!mapEditorRouteContext.selectedInspection;

      // Map / Map+IT projects cannot add findings on the map — hide the "Add Finding" option.
      const projectConfig = mapEditorRouteContext.project?.project_config || 'full';
      // "Add Finding" attaches to an in-app inspection, which only Full Projects have.
      // Every other config (e_swp, map, it_eswp, it_map) lacks a selectable inspection,
      // so the option would only ever sit locked — hide it. (it hides the whole menu below.)
      // Also hidden once the compliance certification is signed — the inspection is closed to new
      // findings, matching the Findings tab and the map editor's findings panel.
      const hideAddFinding = projectConfig !== 'full' || isSignedInspection;
      // Inspection Tracking (it) projects have no map management — hide the map add-options
      // (Add Finding / Control Measure / Pollutant / commentables) entirely.
      const hideMapAddOptions = projectConfig === 'it';

      // Check if feature is newly added (unsaved) - these don't have positionables_id yet
      const isNewFeature = selectedFeature && !selectedFeature.get('positionables_id');

      // empty menu when the positionable type is configured to be read only
      if (selectedFeature && isFeatureReadOnly(selectedFeature)) {
        return [];
      }

      const menuOptions = contextmenuFeature
        ? (selectedFeature && selectedFeature.values_.is_deleted !== undefined && selectedFeature.values_.is_deleted === true)? [] : [
          // SW2-2230 — no-op unless the right-clicked feature is a legend item.
          ...makeAddFindingOptions(contextmenuFeature),
          {
            action: "modify",
            label: geometryType === "Point" ? "Move" : "Edit",
            onClick: () => {
              const feature = selectionContext.getSelectedFeature();

              if (feature) {
                /**
                 * @note First, unset the source model because we
                 * may be in drawing mode and
                 * don't want the item to remain selected
                 * when entering modification mode
                 */
                setPositionableSourceModel(null);

                setMode("modify", { feature });
              }
            },
            isDisabled: (selectedFeature?.values_?.disable_options || (onOlderMapVersion && !isNewFeature) || (!isRecentInsp && !isNewFeature && !allowEditDueToSpecialInspection))? true : false,
            disabledMessage: (selectedFeature?.values_?.disable_options)? `${geometryType === "Point" ? "Move" : "Edit"} option is disabled because this legend item is deleted in future inspections !` : ((onOlderMapVersion && !isNewFeature) || (!isRecentInsp && !isNewFeature && !allowEditDueToSpecialInspection)) ? `Legend item cannot be ${geometryType === "Point" ? "moved" : "edited"} in previous versions of map!` : null
          },
          {
            action: "delete",
            label: "Remove From Map",
            onClick: event => {
              let feature = selectionContext.getSelectedFeature();

              /**
               * @note edge case
               * when {filterModeDataFeature} is set,
               * source the feature from the event
               */
              if (!feature && event.feature) {
                feature = event.feature;
              }

              if (feature) {
                setMode("delete", {
                  feature,
                });
              }
            },
            isDisabled: selectedFeature?.values_?.disable_options ?? false,
            disabledMessage: (selectedFeature?.values_?.disable_options)? `Remove From Map option is disabled because this legend item is deleted in future inspections !` : null
          },
        ] : (isWidth || hideMapAddOptions) ? []
          : (featureContext.tenantHasFeature("Site Map - Editor Updates") && featureContext.tenantHasFeature("Site Map - Finding Locations")) ? [
            // default context menu
            // most click handlers set layer, then display modal featureContext.tenantHasFeature("Site Map - Finding Locations")
            {
              positionableType: "findings",
              label: "Add Finding",
              onClick: () => {
                setPositionableType("findings");
                setMode("createSourceModel", {
                  nextMode: "create",
                  mapEvent: event,
                });
              },
              isDisabled: !hasSelectedInspection,
              disabledMessage: !hasSelectedInspection ? "Please select an inspection before adding a finding." : null,
            },
            {
              positionableType: "control_measure",
              label: "Add Control Measure",
              onClick: () => setPositionableType("control_measure"),
            },
            {
              positionableType: "pollutant",
              label: "Add Pollutant",
              onClick: () => setPositionableType("pollutant"),
            },
            {
              label: `Add ${getDataTypeLabel("commentables", false)}`,
              positionableType: "commentables",
              onClick: () => {
                setPositionableType("commentables");
                setMode("createSourceModel", {
                  nextMode: "create",
                  mapEvent: event,
                });
              },
            },
          ] : featureContext.tenantHasFeature("Site Map - Editor Updates") ? [
            {
              positionableType: "findings",
              label: "Add Finding",
              onClick: () => {
                setPositionableType("findings");
                setMode("createSourceModel", {
                  nextMode: "create",
                  mapEvent: event,
                });
              },
              isDisabled: !hasSelectedInspection,
              disabledMessage: !hasSelectedInspection ? "Please select an inspection before adding a finding." : null,
            },
          ] : featureContext.tenantHasFeature("Site Map - Finding Locations") ? [{
            positionableType: "control_measure",
            label: "Add Control Measure",
            onClick: () => setPositionableType("control_measure"),
          },
          {
            positionableType: "pollutant",
            label: "Add Pollutant",
            onClick: () => setPositionableType("pollutant"),
          },
          {
            label: `Add ${getDataTypeLabel("commentables", false)}`,
            positionableType: "commentables",
            onClick: () => {
              setPositionableType("commentables");
              setMode("createSourceModel", {
                nextMode: "create",
                mapEvent: event,
              });
            },
          }] : [].filter(
            ({ positionableType }) =>
              !dataSourceContext.isReadOnlyDataType(positionableType),
          );

      return hideAddFinding
        ? menuOptions.filter((opt) => opt.positionableType !== "findings")
        : menuOptions;
    },
    [dataSourceContext, isFeatureReadOnly, selectionContext, mapEditorRouteContext, makeAddFindingOptions, isSignedInspection, onOlderMapVersion],
  );

  const getProjectUploadOptions = useCallback(
    (event, contextmenuFeature) => {
      const geometryType =
        contextmenuFeature && contextmenuFeature.get("geometry").getType();
      const selectedFeature = selectionContext.getSelectedFeature();
      const isRecentInsp = 
        mapEditorRouteContext.inspections?.length > 0 && 
        mapEditorRouteContext.inspections[0].id === mapEditorRouteContext.selectedInspection?.id;
      const selectedInspectionId = mapEditorRouteContext.selectedInspection?.id;
      const isInitialInspection = mapEditorRouteContext.inspections?.length === 1;
      const allowEditDueToSpecialInspection = !(selectedInspectionId) || isInitialInspection;
      
      // Check if feature is newly added (unsaved) - these don't have positionables_id yet
      const isNewFeature = selectedFeature && !selectedFeature.get('positionables_id');

      // empty menu when the positionable type is configured to be read only
      if (selectedFeature && isFeatureReadOnly(selectedFeature)) {
        return [];
      }

      return (contextmenuFeature && contextmenuFeature.values_ && (contextmenuFeature.values_.positionable_type === "control_measure" || contextmenuFeature.values_.positionable_type === "pollutant"))
        ? (selectedFeature && selectedFeature.values_.is_deleted !== undefined && selectedFeature.values_.is_deleted === true)? [{label: 'Deleted Legend Item'}] : [
          ...makeAddFindingOptions(contextmenuFeature),
          {
            action: "modify",
            label: geometryType === "Point" ? "Move" : "Edit",
            onClick: () => {
              const feature = selectionContext.getSelectedFeature();

              if (feature) {
                setPositionableSourceModel(null);
                setMode("modify", { feature });
              }
            },
            // SW2-2346: same guard as getOptions above. This is the single-click menu
            // (MapContextMenu.handleSingleClick); getOptions is the right-click one.
            // Guarding only one of them leaves Edit reachable through the other.
            isDisabled: (selectedFeature?.values_?.disable_options || (onOlderMapVersion && !isNewFeature) || (!isRecentInsp && !isNewFeature && !allowEditDueToSpecialInspection))? true : false,
            disabledMessage: (selectedFeature?.values_?.disable_options)? `${geometryType === "Point" ? "Move" : "Edit"} option is disabled because this legend item is deleted in future inspections !` : ((onOlderMapVersion && !isNewFeature) || (!isRecentInsp && !isNewFeature && !allowEditDueToSpecialInspection)) ? `Legend item cannot be ${geometryType === "Point" ? "moved" : "edited"} in previous versions of map!` : null
          },
          {
            action: "delete",
            label: "Remove From Map",
            onClick: event => {
              let feature = selectionContext.getSelectedFeature();

              if (!feature && event.feature) {
                feature = event.feature;
              }

              if (feature) {
                setMode("delete", {
                  feature,
                });
              }
            },
            isDisabled: selectedFeature?.values_?.disable_options ?? false,
            disabledMessage: (selectedFeature?.values_?.disable_options)? `Remove From Map option is disabled because this legend item is deleted in future inspections !` : null
          },
        ] : []
    },
    [dataSourceContext, isFeatureReadOnly, selectionContext, makeAddFindingOptions, mapEditorRouteContext, onOlderMapVersion],
  );

  return <ContextMenuInteraction {...props} getOptions={getOptions} getProjectUploadOptions={getProjectUploadOptions} positionableSourceModel = {positionableSourceModel} />;
}

export function FeatureMenu() {
  return (
    <SelectFeatureOnHover filterModeDataFeature>
      <FeatureMenuUI />
    </SelectFeatureOnHover>
  );
}

export default FeatureMenu;
